import { config } from '../../../config';
import { __ } from '../../utils';
import {
  AppRegistration,
  AssuredWorkload,
  AutoAwesome,
  ContentPasteSearch,
  Poll,
  Architecture,
  CardGiftcard,
} from '@mui/icons-material';
import type { AppPermission } from '@presov.sk/presov-auth-react';
import {
  DomovIcon,
  HlasovanieIcon,
  ProjektyIcon,
  VyhodyIcon,
} from '@presov.sk/presov-ui-react';

export type NavigationLink = {
  icon: React.ReactNode;
  iconActive: React.ReactNode;
  label: string;
  mobileLabel?: string;
  href?: string;
  children?: NavigationLink[];
  hasSomePermission?: AppPermission[];
  hasEveryPermission?: AppPermission[];
  isPublic?: boolean;
  /** tón sekcie pre shell; `accent` je žltá administrácia */
  tone?: 'accent';
};

export const navigationLinks: NavigationLink[] = [
  {
    // Ikony mesta majú jednu podobu, nie plnú a obrysovú - aktívnu položku
    // nesie farba, ktorú dáva `NavIcon` v shelle.
    iconActive: <DomovIcon weight={2} />,
    icon: <DomovIcon weight={2} />,
    label: __('Domov'),
    href: '/',
    isPublic: true,
  },
  // „Moje podnety" tu boli a sú preč zámerne. Sú prvou sekciou profilu, do
  // ktorého sa vchádza menovkou s avatarom vpravo v hlavičke, a druhá cesta
  // k nim je bunka „Moje" nad mapou na hlavnej stránke. V hlavnom menu boli
  // jedinou položkou, ktorú neprihlásený nevidel, a zaberali miesto vedľa
  // dvoch verejných sekcií.
  // Výhody sú dočasne skryté - pozri config.CUSTOM_FEATURES.
  ...(config.CUSTOM_FEATURES.BENEFITS
    ? [
        {
          iconActive: <VyhodyIcon weight={2} />,
          icon: <VyhodyIcon weight={2} />,
          label: __('Výhody'),
          href: '/vyhody',
          isPublic: true,
        },
      ]
    : []),
  {
    iconActive: <HlasovanieIcon weight={2} />,
    icon: <HlasovanieIcon weight={2} />,
    label: __('Hlasovania'),
    href: '/hlasovania',
    isPublic: true,
  },
  // Projekty sú dočasne skryté - pozri config.CUSTOM_FEATURES.
  ...(config.CUSTOM_FEATURES.PROJECTS
    ? [
        {
          iconActive: <ProjektyIcon weight={2} />,
          icon: <ProjektyIcon weight={2} />,
          label: __('Projekty'),
          href: '/projekty',
          isPublic: true,
        },
      ]
    : []),
  {
    iconActive: <AssuredWorkload />,
    icon: <AssuredWorkload />,
    label: __('Administrácia'),
    // Žltý pás pod hlavičkou na desktope a žltá sekcia v mobilnom menu -
    // kreslí ich shell, appka len povie, že táto sekcia je administrácia.
    tone: 'accent',
    hasSomePermission: [
      {
        client_id: config.CLIENT_ID,
        permission: 'pruser:news-categories:manage',
      },
      {
        client_id: config.CLIENT_ID,
        permission: 'pruser:report-categories:manage',
      },
      {
        client_id: config.CLIENT_ID,
        permission: 'pruser:report-categories:users:manage',
      },
      {
        client_id: config.CLIENT_ID,
        permission: 'pruser:reports:read',
      },
      {
        client_id: config.CLIENT_ID,
        permission: 'pruser:reports:manage',
      },
      {
        client_id: config.CLIENT_ID,
        permission: 'pruser:ai-decisions:read',
      },
      // Bez viditeľnej sekcie by tieto oprávnenia otvárali prázdnu Administráciu.
      ...(config.CUSTOM_FEATURES.PROJECTS
        ? ([
            {
              client_id: config.CLIENT_ID,
              permission: 'pruser:projects:manage',
            },
          ] as AppPermission[])
        : []),
      ...(config.CUSTOM_FEATURES.BENEFITS
        ? ([
            {
              client_id: 'cimpo',
              permission: 'admin:benefits:manage',
            },
          ] as AppPermission[])
        : []),
    ],
    children: [
      {
        iconActive: <ContentPasteSearch />,
        icon: <ContentPasteSearch />,
        label: __('Správa podnetov'),
        href: '/administracia/podnety',
        hasSomePermission: [
          {
            client_id: config.CLIENT_ID,
            permission: 'pruser:reports:read',
          },
          {
            client_id: config.CLIENT_ID,
            permission: 'pruser:reports:manage',
          },
        ],
      },
      // Kategórie podnetov tu nie sú: vchádza sa do nich tlačidlom
      // „Spravovať kategórie" v hlavičke zoznamu podnetov, lebo s podnetmi
      // patria k sebe a správca kategórie by inak dostal položku navigácie
      // navyše len kvôli členom svojej kategórie.
      // Správa výhod je dočasne skrytá - pozri config.CUSTOM_FEATURES.
      ...(config.CUSTOM_FEATURES.BENEFITS
        ? ([
            {
              iconActive: <CardGiftcard />,
              icon: <CardGiftcard />,
              label: __('Správa výhod'),
              href: '/administracia/vyhody',
              hasSomePermission: [
                {
                  client_id: 'cimpo',
                  permission: 'admin:benefits:manage',
                },
              ],
            },
          ] as NavigationLink[])
        : []),
      {
        iconActive: <Poll />,
        icon: <Poll />,
        label: __('Správa hlasovaní'),
        href: '/administracia/hlasovania',
        hasSomePermission: [
          {
            client_id: config.CLIENT_ID,
            permission: 'pruser:votings:manage',
          },
        ],
      },
      // Správa projektov je dočasne skrytá - pozri config.CUSTOM_FEATURES.
      ...(config.CUSTOM_FEATURES.PROJECTS
        ? ([
            {
              iconActive: <Architecture />,
              icon: <Architecture />,
              label: __('Správa projektov'),
              href: '/administracia/projekty',
              hasSomePermission: [
                {
                  client_id: config.CLIENT_ID,
                  permission: 'pruser:projects:manage',
                },
              ],
            },
          ] as NavigationLink[])
        : []),
      {
        iconActive: <AppRegistration />,
        icon: <AppRegistration />,
        label: __('Správa noviniek'),
        href: '/administracia/kategorie-novinky',
        hasSomePermission: [
          {
            client_id: 'digiobcan',
            permission: 'pruser:news-categories:manage',
          },
        ],
      },
      {
        iconActive: <AutoAwesome />,
        icon: <AutoAwesome />,
        label: __('AI rozhodnutia'),
        href: '/administracia/ai',
        hasSomePermission: [
          {
            client_id: config.CLIENT_ID,
            permission: 'pruser:ai-decisions:read',
          },
        ],
      },
    ],
  },
];
